import React, { useRef, useState } from "react";
import {
  Text,
  Navbar as MNavbar,
  Divider,
  Box,
  Tooltip,
  ActionIconVariant,
  ActionIcon,
  Anchor,
  MantineTheme,
  ScrollArea,
  ThemeIcon,
} from "@mantine/core";
import { FaSun, FaMoon } from "react-icons/fa";
import { UnstyledButton, Group, useMantineColorScheme } from "@mantine/core";
import { useNavigate, ROUTER_BASE } from "./Components/Link";
import { useGlobalStatusContext } from "./GlobalStore";
import { useIsSmallScreen } from "./Components/Utils";
import Icon from "./Components/Icon";
// import { ChainSwitch } from "./Components/ChainSwitch";

interface MainLinkProps {
  icon?: React.ReactNode;
  label: React.ReactNode;
  href?: string;
  isExtend?: boolean;
  navExpandOnClickDid?: boolean;
  onClick?: React.ReactEventHandler;
  alt?: string;
  external?: boolean;
  inline?: boolean;
  iconVariant?: ActionIconVariant;
  id?: string;
  preload?: Function;
}

function BottomLink({
  icon,
  label,
  href,
  isExtend,
  navExpandOnClickDid,
  onClick,
  alt,
  external,
  inline,
  iconVariant = "transparent",
  id,
  preload,
}: MainLinkProps) {
  const [tooltipOpened] = useState(false);

  const iconWrap = (
    <ActionIcon
      variant={iconVariant}
      sx={(theme) => ({
        borderRadius: "50%",
        backgroundColor:
          theme.colorScheme === "dark"
            ? theme.colors.dark[6]
            : theme.colors.gray[2],
        color: theme.colors.gray[6],
        "&:hover": {
          color: theme.white,
          backgroundColor: "#44D7B6",
        },
      })}
    >
      {icon}
    </ActionIcon>
  );

  const sx = (theme: MantineTheme) => ({
    display: "block",
    width: "100%",
    textDecoration: "none",
    padding: theme.spacing.xs,
    borderRadius: "50%",
    color: theme.colorScheme === "dark" ? theme.white : theme.black,

    ...(href &&
    (window.location.pathname + window.location.hash).endsWith(href) &&
    !external
      ? {
          backgroundColor: "red",
        }
      : {
          "&:hover": {
            backgroundColor: "rgba(68,215,182,0.10)",
            textDecoration: "none",
          },
        }),
  });

  const btn = (
    <Anchor href={href} target="_blank" sx={sx}>
      {iconWrap}
    </Anchor>
  );

  return (
    <Tooltip
      opened={tooltipOpened}
      label={alt}
      position={inline ? (isExtend ? "top" : "right") : "right"}
      // placement="center"
      withArrow
      // styles={{
      //   body: {
      //     pointerEvents: "none",
      //     textAlign: "center",
      //   },
      //   root: {
      //     display: inline && isExtend ? "inline-block" : "block",
      //   },
      // }}
    >
      {btn}
    </Tooltip>
  );
}

function MainLink({
  icon,
  label,
  href,
  isExtend,
  navExpandOnClickDid,
  onClick,
  alt,
  external,
  inline,
  iconVariant = "transparent",
  id,
  preload,
}: MainLinkProps) {
  const navigate = useNavigate();
  const { navExpand, setNavExpand } = useGlobalStatusContext();
  const [tooltipOpened, setTooltipOpened] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { setNavVisibleOnSmallScreen } = useGlobalStatusContext();

  const ref = useRef(null);

  const onClickFunc: React.MouseEventHandler<HTMLButtonElement> = (...args) => {
    setTooltipOpened(false);
    if (!inline && !external && id !== "expand") {
      setNavVisibleOnSmallScreen(false);
    }
    if (href && !external) {
      if (window.location.pathname.startsWith("/eigentx/")) {
        window.location.href = href;
      } else {
        navigate(href);
      }
    }
    if (navExpandOnClickDid !== undefined) {
      setNavExpand(!!navExpandOnClickDid);
    }
    if (onClick) {
      onClick?.apply(ref, args);
    }
  };

  const sx = (theme: MantineTheme) => ({
    display: "block",
    width: "100%",
    textDecoration: "none",
    padding: theme.spacing.xs,
    borderRadius: theme.radius.md,
    color: theme.colorScheme === "dark" ? theme.white : theme.black,
    ...(href &&
    (window.location.pathname + window.location.hash).endsWith(href) &&
    !external
      ? {
          backgroundColor:
            theme.colorScheme === "dark"
              ? theme.colors.dark[5]
              : theme.colors.gray[2],
          ".iconfont": {
            color: theme.colorScheme === "dark" ? "#44D7B6" : "#54B498",
          },
        }
      : {
          "&:hover": {
            backgroundColor:
              theme.colorScheme === "dark"
                ? theme.colors.dark[5]
                : theme.colors.gray[1],
            textDecoration: "none",
            ".iconfont": {
              color: theme.colorScheme === "dark" ? "#44D7B6" : "#54B498",
            },
          },
        }),
  });
  const iconWrap = (
    <Group position={isExtend ? "left" : "center"}>
      {icon ? (
        <ActionIcon
          variant={iconVariant}
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === "dark"
                ? theme.colors.dark[6]
                : theme.colors.gray[0],
            color:
              theme.colorScheme === "dark"
                ? theme.colors.gray[6]
                : theme.colors.gray[inline ? 6 : 8],
            "&:hover": {
              color: "#44D7B6",
            },
          })}
        >
          {icon}
        </ActionIcon>
      ) : null}
      {isExtend && !inline ? <Text size="sm">{label}</Text> : null}
    </Group>
  );

  const btn = external ? (
    <Box
      onMouseEnter={(event: any) => {
        if (navExpand === false || !isExtend || inline) {
          setTooltipOpened(true);
        } else {
          setTooltipOpened(false);
        }
      }}
      onMouseLeave={() => {
        setTooltipOpened(false);
      }}
    >
      <Anchor href={href} target="_blank" sx={sx}>
        {iconWrap}
      </Anchor>
    </Box>
  ) : (
    <UnstyledButton
      ref={ref}
      onTouchStart={() => {
        setIsMobile(true);
      }}
      onMouseEnter={(event: React.MouseEvent<HTMLButtonElement>) => {
        if (isMobile) {
          onClickFunc.call(ref, event);
        } else {
          if (navExpand === false || !isExtend || inline) {
            setTooltipOpened(true);
          } else {
            setTooltipOpened(false);
          }
          preload && preload();
        }
      }}
      onMouseLeave={() => {
        setTooltipOpened(false);
      }}
      onClick={onClickFunc}
      sx={sx}
    >
      <Group position={isExtend ? "left" : "center"}>
        {icon ? (
          <ThemeIcon
            sx={(theme) => ({
              backgroundColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[6]
                  : theme.colors.gray[0],
              color:
                theme.colorScheme === "dark"
                  ? theme.colors.gray[6]
                  : theme.colors.gray[inline ? 6 : 8],
              "&:hover": {
                color: "#44D7B6",
              },
            })}
          >
            {icon}
          </ThemeIcon>
        ) : null}
        {isExtend && !inline ? <Text size="sm">{label}</Text> : null}
      </Group>
    </UnstyledButton>
  );
  return alt ? (
    <Tooltip
      opened={tooltipOpened}
      withinPortal
      label={alt}
      position={inline ? (isExtend ? "top" : "right") : "right"}
      withArrow
      styles={{
        tooltip: {
          display: inline && isExtend ? "inline-block" : "block",
        },
      }}
    >
      {btn}
    </Tooltip>
  ) : (
    btn
  );
}

const NAVBAR_EXPAND_WIDTH = 260;
const NAVBAR_COLLAPSE_WIDTH = 60;
export function MainLinks({ isExtend }: { isExtend?: boolean }) {
  const { chain } = useGlobalStatusContext();
  const ref = useRef<HTMLDivElement>(null);
  const isSmallScreen = useIsSmallScreen();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";

  const linkData1: Array<MainLinkProps> = [
    {
      icon: <Icon name="icon-home" size={16} />,
      label: "Home",
      alt: "Home",
      href: "/",
      onClick: () => {
        document.getElementById("top")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      },
    },
    {
      icon: <Icon name="icon-sandwich" size={16} />,
      label: "Sandwich",
      alt: "Sandwich",
      href: `/${chain}/sandwich`,
      preload: () => {
        import("./Sandwich/SubjectPage");
      },
    },
    {
      icon: <Icon name="icon-liquidation" size={16} />,
      label: "Liquidation",
      alt: "Liquidation",
      href: `/${chain}/liquidation`,
      preload: () => {
        import("./Liquidation/MarketPage");
      },
    },
    /*
    {
      icon: <Icon name="icon-lending" size={16} />,
      label: "Lending",
      alt: "Lending",
      href: `/${chain}/lending`,
      preload: () => {
        import("./Lending/HomePage");
      },
    },
    */
    {
      icon: <Icon name="icon-flash" size={16} />,
      label: "Flashloan",
      alt: "Flashloan",
      href: `/${chain}/flashloan`,
      preload: () => {
        import("./Flashloan/MarketPage");
      },
    },
    {
      icon: <Icon name="icon-live-stream" size={16} />,
      label: "MEV Live-stream",
      alt: "MEV Live-stream",
      href: `/${chain}/txr`,
      preload: () => {
        import("./Transaction/TransactionRealtimePage");
      },
    },
  ];
  const linkData2: Array<MainLinkProps> = [
    {
      icon: <Icon name="icon-tokens" size={16} />,
      label: "Tokens",
      alt: "Tokens",
      href: `/${chain}/tokens/hot`,
      preload: () => {
        import("./Token/TokenPage");
      },
    },
    {
      icon: <Icon name="icon-pools" size={16} />,
      label: "Pools",
      alt: "Pools",
      href: `/${chain}/pools/hot`,
      preload: () => {
        import("./Pool/PoolPage");
      },
    },
    /*
    {
      icon: <Icon name="icon-malicious" size={16} />,
      label: "Malicious Tokens",
      alt: "Malicious Tokens",
      href: `/${chain}/tokens/malicious`,
      preload: () => {
        import("./Token/TokenPage");
      },
    },
    */
    {
      icon: <Icon name="icon-analysis" size={16} />,
      label: <Group spacing={10}>EigenTx</Group>,
      alt: "EigenTx",
      href: `/eigentx`,
    },
  ];

  const linkDataReport: Array<MainLinkProps> = [
    {
      icon: <Icon name="icon-research" size={16} />,
      label: (
        <Group spacing={10}>
          Research
          {/* <Image width={20} height={8.8} src="/images/icon/new.svg" /> */}
        </Group>
      ),
      alt: "Research",
      href: `https://eigenphi.substack.com/s/defi-research`,
      external: true,
    },
    // {
    //   icon: <Icon name="icon-EigenAPI" size={16} />,
    //   label: (
    //     <Group spacing={10}>
    //       EigenAPI
    //       <Image width={20} height={8.8} src="/images/icon/new.svg" />
    //     </Group>
    //   ),
    //   alt: "EigenAPI",
    //   href: "https://eigenapi.io/",
    //   external: true,
    // },
  ];

  const linkData3: Array<MainLinkProps> = [
    // {
    //   icon: <Icon name="icon-bounties" size={14} />,
    //   label: "Bug and Improvement Bounty",
    //   alt: "Bug and Improvement",
    //   href: `/${ROUTER_BASE}/bounty/BugImprovement`,
    //   external: true,
    // },
    {
      icon: <Icon name="icon-Reports" size={14} />,
      label: "Reports",
      alt: "Reports",
      href: `/${ROUTER_BASE}/${chain}/dailyReport`,
      external: true,
    },
    {
      icon: <Icon name="icon-Classroom" size={14} />,
      label: "Classroom",
      alt: "Classroom",
      href: "https://eigenphi-1.gitbook.io/classroom/",
      external: true,
    },
    {
      icon: <Icon name="icon-Newsletter" size={14} />,
      label: "Newsletter",
      alt: "Newsletter",
      href: "https://eigenphi.substack.com/",
      external: true,
    },
    {
      icon: <Icon name="icon-UserGuide" size={14} />,
      label: "User Guide",
      alt: "User Guide",
      href: "https://eigenphi-1.gitbook.io/arbitrage-scan-user-guide/",
      external: true,
    },
  ];

  const links1 = linkData1.map((link) => (
    <MainLink
      {...link}
      key={link.label?.toString()}
      isExtend={isExtend}
      navExpandOnClickDid={link.navExpandOnClickDid}
    />
  ));
  const links2 = linkData2.map((link) => (
    <MainLink
      {...link}
      key={link.label?.toString()}
      isExtend={isExtend}
      navExpandOnClickDid={link.navExpandOnClickDid}
    />
  ));
  const linkReport = linkDataReport.map((link) => (
    <MainLink
      {...link}
      key={link.label?.toString()}
      isExtend={isExtend}
      navExpandOnClickDid={link.navExpandOnClickDid}
    />
  ));
  const links3 = linkData3.map((link) => (
    <MainLink
      {...link}
      key={link.label?.toString()}
      isExtend={isExtend}
      navExpandOnClickDid={link.navExpandOnClickDid}
    />
  ));

  return (
    <Group position="center" sx={{ flexDirection: "column" }} ref={ref}>
      <Box
        sx={{
          width: isSmallScreen
            ? window.innerWidth - 20
            : isExtend === false
            ? 50
            : 240,
        }}
      >
        {links1}
        <Divider
          sx={(theme) => ({
            borderTopColor:
              theme.colorScheme === "dark"
                ? theme.colors.dark[6]
                : theme.colors.gray[2],
            marginLeft: 10,
            marginRight: 10,
            marginTop: 15,
            marginBottom: 15,
          })}
        />
        <Text
          sx={{
            padding: "0 10px",
            color: "#909296",
            fontSize: 12,
          }}
        >
          Tools
        </Text>
        {links2}
        <Divider
          sx={(theme) => ({
            borderTopColor:
              theme.colorScheme === "dark"
                ? theme.colors.dark[6]
                : theme.colors.gray[2],
            marginLeft: 10,
            marginRight: 10,
            marginTop: 15,
            marginBottom: 15,
          })}
        />
        {linkReport}

        {/* {isExtend && !isSmallScreen ? (
          <Group position="center" sx={{ flexDirection: "column" }}>
            <Anchor component={Link} to={`/${chain}/eigentx`}>
              <Image
                style={{ marginTop: 20, width: 214, height: 80 }}
                src="/images/icon/eigentx-banner.svg"
              />
            </Anchor>
          </Group>
        ) : null} */}
        {isSmallScreen ? (
          <>
            <Divider
              sx={(theme) => ({
                borderTopColor:
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[6]
                    : theme.colors.gray[2],
                marginLeft: 10,
                marginRight: 10,
                marginTop: 15,
                marginBottom: 15,
              })}
            />
            <MainLink
              key="theme"
              isExtend={isExtend}
              icon={dark ? <FaSun /> : <FaMoon />}
              label={dark ? "Light Theme" : "Dark Theme"}
              alt={dark ? "Light Theme" : "Dark Theme"}
              onClick={() => {
                toggleColorScheme();
              }}
            />
          </>
        ) : null}
        {isSmallScreen ? (
          <>
            <Divider
              sx={(theme) => ({
                borderTopColor:
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[6]
                    : theme.colors.gray[2],
                marginLeft: 10,
                marginRight: 10,
                marginTop: 15,
                marginBottom: 15,
              })}
            />
            {links3}
          </>
        ) : null}
      </Box>
    </Group>
  );
}

export function BottomLinks({ isExtend }: { isExtend?: boolean }) {
  const isSmallScreen = useIsSmallScreen();
  const inlineRef = useRef<HTMLDivElement>(null);

  const linkData4: Array<MainLinkProps> = [
    {
      icon: <Icon name="icon-twitter" size={14} />,
      label: "Twitter",
      alt: "Twitter",
      href: `https://twitter.com/eigenphi`,
      external: true,
      inline: true,
    },
    {
      icon: <Icon name="icon-discord" size={14} />,
      label: "Discord",
      alt: "Discord",
      href: `https://discord.gg/2B96UzwpHB`,
      external: true,
      inline: true,
    },
    {
      icon: <Icon name="icon-medium" size={14} />,
      label: "Medium",
      alt: "Medium",
      href: `https://medium.com/@eigenphi/`,
      external: true,
      inline: true,
    },
    {
      icon: <Icon name="icon-mirror" size={14} />,
      label: "Mirror",
      alt: "Mirror",
      href: `https://mirror.xyz/0xc19565163aFdEe3783FC970E4Bd0275B11848d34`,
      external: true,
      inline: true,
    },
  ];
  const links4 = linkData4.map((link) => (
    <BottomLink
      {...link}
      key={link.label?.toString()}
      isExtend={isExtend}
      navExpandOnClickDid={link.navExpandOnClickDid}
    />
  ));
  return (
    <>
      {isExtend ? null : (
        <Divider
          sx={(theme) => ({
            borderTopColor:
              theme.colorScheme === "dark"
                ? theme.colors.dark[6]
                : theme.colors.gray[2],
            marginLeft: 10,
            marginRight: 10,
            marginTop: 15,
            marginBottom: 15,
          })}
        />
      )}
      <Box
        sx={(theme) => ({
          width: isSmallScreen
            ? "100%"
            : isExtend
            ? NAVBAR_EXPAND_WIDTH
            : NAVBAR_COLLAPSE_WIDTH,
          background:
            theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
          padding: "10px 10px 20px 10px",
          borderRight: `1px solid ${
            theme.colorScheme === "dark"
              ? theme.colors.dark[5]
              : theme.colors.gray[2]
          }`,
        })}
        ref={inlineRef}
      >
        <Group position="center" spacing={"xs"}>
          {links4}
        </Group>
      </Box>
    </>
  );
}

export default function Navbar({ isExtend }: { isExtend?: boolean }) {
  const isSmallScreen = useIsSmallScreen();

  return (
    <MNavbar
      hiddenBreakpoint={"sm"}
      width={{
        base: isSmallScreen
          ? "100%"
          : isExtend
          ? NAVBAR_EXPAND_WIDTH
          : NAVBAR_COLLAPSE_WIDTH,
      }}
      sx={{
        marginTop: 80,
        top: 0,
        paddingBottom: isSmallScreen ? 100 : 0,
      }}
      fixed
    >
      {/* {isSmallScreen ? null : (
        <MNavbar.Section
          sx={{
            width: isSmallScreen
              ? "100%"
              : isExtend
              ? NAVBAR_EXPAND_WIDTH
              : NAVBAR_COLLAPSE_WIDTH,
            background:
              theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
            padding: isExtend ? 10 : 5,
          }}
        >
          <ChainSwitch isCollapse={!isExtend} />
        </MNavbar.Section>
      )} */}
      <MNavbar.Section mt="xs" grow component={ScrollArea}>
        {/* 移动端让侧边栏始终是Extend状态 */}
        <MainLinks isExtend={isExtend || isSmallScreen}></MainLinks>
      </MNavbar.Section>
      {/* <MNavbar.Section>
        <BottomLinks isExtend={isExtend}></BottomLinks>
      </MNavbar.Section> */}
    </MNavbar>
  );
}
